import React, { useState } from 'react';
import {
  Bullseye,
  EmptyState,
  EmptyStateBody,
  MenuToggle,
  Select,
  SelectList,
  SelectOption,
  Spinner,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { ChartLineIcon } from '@patternfly/react-icons';
import { useAdminGpuCostLedger } from '../../../hooks/useAdminVerdaServers';
import { formatDateTime } from '../../../utils/dateFormat';
import { formatCurrencyAmount } from '../../../utils/money';
import { runwayDisplay } from './verdaGpuServers';
import { VerdaErrorAlert, VerdaRefreshButton } from './VerdaTabParts';

/** Meter runs happen every 5 minutes: 96 ≈ 8 h, 288 ≈ 24 h, 500 (the backend maximum) ≈ 42 h. */
const LIMIT_OPTIONS = [
  { value: 96, label: '96 runs (≈ 8 h)' },
  { value: 288, label: '288 runs (≈ 24 h)' },
  { value: 500, label: '500 runs (≈ 42 h)' },
];

interface VerdaCostLedgerTabProps {
  /** Defers the fetch until the staff gate has passed. */
  isStaff: boolean;
}

const optionalEur = (value: string | null): string => (value === null ? '—' : formatCurrencyAmount(value, 'EUR'));

/**
 * Admin › Verda › Cost ledger — one entry per usage-meter run, newest first
 * (`GET /admin/verda/cost-ledger`, a database read): hours billed, their Verda cost, the Verda balance,
 * the burn rate and the runway. A runway under 24 h refuses starts (warning), under 6 h is danger; an
 * unknown runway is a warning, never danger. A run whose Verda listing failed shows in red.
 *
 * List-table decision (Bruno, 2026-09-28): no search and no pagination — it is a time series, bounded
 * by the "runs" select (96 / 288 / 500).
 */
const VerdaCostLedgerTab: React.FunctionComponent<VerdaCostLedgerTabProps> = ({ isStaff }) => {
  const [limit, setLimit] = useState(96);
  const [isLimitOpen, setIsLimitOpen] = useState(false);
  const { entries, loading, error, refetch } = useAdminGpuCostLedger(limit, isStaff);

  const renderBody = () => {
    if (error) {
      return <VerdaErrorAlert what="cost ledger" message={error} />;
    }
    if (entries === null) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading the cost ledger" />
        </Bullseye>
      );
    }
    if (entries.length === 0) {
      return (
        <EmptyState titleText="No meter runs yet" icon={ChartLineIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The usage meter has not recorded a run.</EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <Table aria-label="GPU cost ledger" variant="compact">
        <Thead>
          <Tr>
            <Th>Run</Th>
            <Th>Hours billed</Th>
            <Th>Cost billed</Th>
            <Th>Verda balance</Th>
            <Th>Burn / h</Th>
            <Th>Runway</Th>
            <Th>Instances listed</Th>
          </Tr>
        </Thead>
        <Tbody>
          {entries.map((entry) => {
            const runway = runwayDisplay(entry);
            return (
              <Tr key={entry.run_at}>
                <Td dataLabel="Run" modifier="nowrap">
                  {formatDateTime(entry.run_at)}
                </Td>
                <Td dataLabel="Hours billed">{entry.hours_billed}</Td>
                <Td dataLabel="Cost billed" modifier="nowrap">
                  {formatCurrencyAmount(entry.cost_billed, 'EUR')}
                </Td>
                <Td dataLabel="Verda balance" modifier="nowrap">
                  {optionalEur(entry.verda_balance)}
                </Td>
                <Td dataLabel="Burn / h" modifier="nowrap">
                  {optionalEur(entry.burn_per_hour)}
                </Td>
                <Td dataLabel="Runway" modifier="nowrap">
                  <span
                    className={
                      runway.severity === 'danger'
                        ? 'pf-v6-u-text-color-status-danger'
                        : runway.severity === 'warning'
                          ? 'pf-v6-u-text-color-status-warning'
                          : undefined
                    }
                  >
                    {runway.text}
                  </span>
                </Td>
                <Td dataLabel="Instances listed">
                  {entry.instances_listed === null ? (
                    <span className="pf-v6-u-text-color-status-danger">Listing failed</span>
                  ) : (
                    entry.instances_listed
                  )}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Toolbar>
        <ToolbarContent>
          <ToolbarItem>
            <Select
              isOpen={isLimitOpen}
              selected={limit}
              onSelect={(_event, value) => {
                if (typeof value === 'number') {
                  setLimit(value);
                }
                setIsLimitOpen(false);
              }}
              onOpenChange={(open) => setIsLimitOpen(open)}
              toggle={(toggleRef) => (
                <MenuToggle
                  ref={toggleRef}
                  onClick={() => setIsLimitOpen(!isLimitOpen)}
                  isExpanded={isLimitOpen}
                  aria-label="Number of meter runs shown"
                >
                  {LIMIT_OPTIONS.find((option) => option.value === limit)?.label ?? `${limit} runs`}
                </MenuToggle>
              )}
            >
              <SelectList>
                {LIMIT_OPTIONS.map((option) => (
                  <SelectOption key={option.value} value={option.value}>
                    {option.label}
                  </SelectOption>
                ))}
              </SelectList>
            </Select>
          </ToolbarItem>
          <ToolbarItem>
            <VerdaRefreshButton loading={loading} onRefresh={refetch} what="cost ledger" />
          </ToolbarItem>
        </ToolbarContent>
      </Toolbar>
      {renderBody()}
    </>
  );
};

export default VerdaCostLedgerTab;
